import { useRef, useState, useCallback, useEffect } from "react";
import { aplicarFocoContinuo } from "@utils";

const TIPOS_ACEITOS = ["image/jpeg", "image/png"];
const TAMANHO_MAXIMO_BYTES = 10 * 1024 * 1024;

const possuiMaisDeUmaCamera = async () => {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.enumerateDevices
  )
    return false;
  try {
    const dispositivos = await navigator.mediaDevices.enumerateDevices();
    return (
      dispositivos.filter((dispositivo) => dispositivo.kind === "videoinput")
        .length > 1
    );
  } catch {
    return false;
  }
};

const RESOLUCAO_FULL_HD = { width: { ideal: 1920 }, height: { ideal: 1080 } };

const abrirCamera = async (facingMode) => {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode, ...RESOLUCAO_FULL_HD },
  });
  await aplicarFocoContinuo(stream.getVideoTracks()[0]);
  return stream;
};

const isStreamFrontal = (stream, facingModePedido) => {
  const facingModeReal = stream.getVideoTracks()[0]?.getSettings?.().facingMode;
  return (facingModeReal || facingModePedido) === "user";
};

export const useCameraCapture = (facingModeInicial = "user") => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [foto, setFoto] = useState(null);
  const [error, setError] = useState(null);
  const [uploadErro, setUploadErro] = useState(null);
  const [facingMode, setFacingMode] = useState(facingModeInicial);
  const [podeAlternarCamera, setPodeAlternarCamera] = useState(false);
  const [isCameraFrontal, setIsCameraFrontal] = useState(false);

  const parar = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
  }, []);

  const iniciar = useCallback(async () => {
    setError(null);
    try {
      const stream = await abrirCamera(facingMode);
      streamRef.current = stream;
      setIsCameraFrontal(isStreamFrontal(stream, facingMode));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setPodeAlternarCamera(await possuiMaisDeUmaCamera());
    } catch (err) {
      setError(err);
    }
  }, [facingMode]);

  const alternarCamera = useCallback(async () => {
    const novoFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(novoFacingMode);
    setError(null);
    parar();

    try {
      const stream = await abrirCamera(novoFacingMode);
      streamRef.current = stream;
      setIsCameraFrontal(isStreamFrontal(stream, novoFacingMode));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError(err);
    }
  }, [facingMode, parar]);

  const processarArquivo = useCallback((arquivo) => {
    if (!arquivo) return;

    setUploadErro(null);

    if (!TIPOS_ACEITOS.includes(arquivo.type)) {
      setUploadErro("Formato inválido. Envie um arquivo JPEG ou PNG.");
      return;
    }

    if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
      setUploadErro("Arquivo muito grande. O tamanho máximo permitido é 10MB.");
      return;
    }

    const leitor = new FileReader();
    leitor.onload = () => setFoto(leitor.result);
    leitor.onerror = () =>
      setUploadErro("Não foi possível ler o arquivo selecionado.");
    leitor.readAsDataURL(arquivo);
  }, []);

  const capturar = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    setFoto(canvas.toDataURL("image/jpeg"));
  }, []);

  const tirarNovamente = useCallback(() => {
    setFoto(null);
    setUploadErro(null);
  }, []);

  useEffect(() => parar, [parar]);

  return {
    videoRef,
    foto,
    error,
    uploadErro,
    facingMode,
    isCameraFrontal,
    podeAlternarCamera,
    iniciar,
    capturar,
    processarArquivo,
    tirarNovamente,
    parar,
    alternarCamera,
  };
};
